<template>
  <div class="dashboard full-page-width" style="padding-top: 40px;" v-if="loaded">
    <div class="row m-b-40">
      <div class="col-xs-8">
        <div class="clear-float clearfix">
          <h2 class="m-b-20 section-title float-left">{{ $t('Activity') }}</h2>
          <span class="float-right text-right widget-top-link"  @click="$router.push('shipments/outbound')">{{ $t('view all shipments')}} ></span>
        </div>

        <div class="card p-40" style="height: 383px;">
          <div v-if="isActivityGraph">
            <ChartLegend :options="areaChartLegend" />
            <VueApexCharts
              width="100%"
              height="288"
              type="area"
              :options="areaChartOptions"
              :series="areaChartsSeries" />
          </div>
          <div class="vertical-align-box" v-else>
            <img class="card-info-image" src="@/assets/images/activity-no-data.png" :class="{fadeinout : isLoadingActivity}"/>
            <p class="card-info-text" v-if="!isLoadingActivity">
              {{ $t('activity_graph_start_text') }}
            </p>
          </div>
        </div>
      </div>
      <div class="col-xs-4">
        <div class="clear-float clearfix">
          <h2 class="m-b-20 section-title float-left">{{ $t('Deviations') }}</h2>
          <span class="float-right text-right widget-top-link"  @click="$router.push({ path: 'shipments/outbound', query: {search : getDeviationsQueryString()}})">{{ $t('view_all_deviations')}} ></span>
        </div>
        <ul class="list-table" v-if="isDeviationsGraph">
          <li
            class="list-table-item ctz"
            @click="goToShipment(item.shipment_id)"
            v-for="(item, index) in get_deviations_graph" :key="`item-${index}`">
            <div class="table-cell small">
              <Icon
                :circle=true
                class="color-warning"
                name="warningSign"
                size="20"/>
            </div>
            <div class="table-cell">
              <p>
            <span class="main-info">
              {{ `${item.customer_company_name ? item.customer_company_name : item.customer_name}` }}
            </span>
                <span class="additional-info">{{ `${item.message ? getStatusMessageText(item.message) : ''}` }}</span>
              </p>
            </div>
          </li>
        </ul>
        <div class="card p-40 vertical-align-box" style="height: 383px;" v-else>
          <img class="card-info-image" src="@/assets/images/deviations-no-data.png" :class="{fadeinout : isLoadingDeviation}" />
          <p class="card-info-text" v-if="!isLoadingDeviation">
            {{ $t('deviation_shipments_start_text') }}
          </p>
        </div>
      </div>
    </div>
    <div class="row m-b-40">
      <div class="col-xs-4">
        <h2 class="m-b-20 section-title">{{ $t('Performance') }}</h2>
        <div class="card p-40 relative" style="height: 630px;">
          <div v-if="isPerformanceGraph" class="performance-graph">
            <div class="shipment-total-chart">
              <span class="total-text">{{ get_performance_graph.total_shipments }}</span>
              <span class="total-subtext">{{ $t('Shipments') }}</span>
            </div>
            <VueApexCharts
              :options="performanceChartOptions"
              width="100%"
              :series="performanceChartsSeries" />
            <ChartLegend type="vertical" :extendedOptions="performanceChartLegend" />
          </div>
          <div class="vertical-align-box" v-else>
            <img class="card-info-image" src="@/assets/images/performance-no-data.png" :class="{fadeinout : isLoadingPerformance}"  />
            <p class="card-info-text" v-if="!isLoadingPerformance">
              {{ $t('performance_graph_start_text')}}
            </p>
          </div>
        </div>
      </div>
      <div class="col-xs-8">
        <h2 class="m-b-20 section-title">{{ $t('Delivery destinations') }}</h2>
        <div class="card p-40-20 map-graph-container" style="height: 630px;">
          <div v-if="doBubbles" class="relative">
            <!--v-if="projectionArea != 'world'"-->
            <div
              v-if="projectionArea != 'world'"
              class="reuturnZoom"
              @click="zoomToWorld">
              <Icon
                style="cursor: pointer;"
                name="zoomOut"
                size="24"
                color="#000" />
            </div>
            <vue-datamaps
              :key="`map-data-${updateMap}`"
              :geographyConfig="geographyConfig"
              :bubblesConfig="bubblesConfig"
              :fills="fills"
              @custom:popup-bubble="popupTemplate"
              @click:bubble="handleBubbleClick"
              :setProjection="setProjection(projectionArea)"
              bubbles>
              <div
                slot="hoverBubbleInfo"
                class="apexcharts-tooltip apexcharts-theme-light apexcharts-active">
                <div class="apexcharts-tooltip-title" style="font-size: 12px;">
                  {{ popupData.tooltipTitle }}
                </div>
                <div class="apexcharts-tooltip-series-group apexcharts-active" style="order: 1; display: flex;">
                  <span class="apexcharts-tooltip-marker" :style="{background: deliveredColor}"></span>
                  <div class="apexcharts-tooltip-text" style="font-family: Helvetica, Arial, sans-serif; font-size: 12px;">
                    <div class="apexcharts-tooltip-y-group">
                      <span class="apexcharts-tooltip-text-y-label">
                        {{ $t('Delivered') }}:
                      </span>
                      <span class="apexcharts-tooltip-text-y-value">
                        {{ popupData.deliveredCount }}
                      </span>
                    </div>
                  </div>
                </div>
                <div class="apexcharts-tooltip-series-group apexcharts-active" style="order: 2; display: flex;">
                  <span class="apexcharts-tooltip-marker" :style="{background: notDeliveredColor}"></span>
                  <div class="apexcharts-tooltip-text" style="font-family: Helvetica, Arial, sans-serif; font-size: 12px;">
                    <div class="apexcharts-tooltip-y-group">
                      <span class="apexcharts-tooltip-text-y-label">
                        {{ $t('on_the_way') }}:
                      </span>
                      <span class="apexcharts-tooltip-text-y-value">
                        {{ popupData.onTheWayCount }}
                      </span>
                    </div>
                  </div>
                </div>
                <div class="apexcharts-tooltip-series-group apexcharts-active" style="order: 3; display: flex;">
                  <span class="apexcharts-tooltip-marker" style="background-color: rgb(237, 86, 86);"></span>
                  <div class="apexcharts-tooltip-text" style="font-family: Helvetica, Arial, sans-serif; font-size: 12px;">
                    <div class="apexcharts-tooltip-y-group">
                      <span class="apexcharts-tooltip-text-y-label">
                        {{ $t('Deviations') }}:
                      </span>
                      <span class="apexcharts-tooltip-text-y-value">
                        {{ popupData.deviations }}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </vue-datamaps>
          </div>
          <div class="vertical-align-box" v-else>
            <img class="card-info-image" src="@/assets/images/destinations-no-data.png" :class="{fadeinout : isLoadingMap}" />
            <p class="card-info-text" v-if="!isLoadingMap">
              {{ $t('map_graph_start_text')}}
            </p>
          </div>
        </div>
      </div>
    </div>
    <div class="row m-b-20">
      <div class="col-xs-8">
        <h2 class="m-b-20 section-title">{{ $t('Carriers') }}</h2>
        <div class="card p-40 relative" style="min-height: 480px;">
          <div v-if="isCarrierGraph && !isLoadingCarrier">
            <ChartLegend :options="carriersChartLegend" />
            <VueApexCharts
              width="100%"
              height="340"
              type="bar"
              :options="carriersChartOptions"
              :series="carriersChartsSeries" />
          </div>
          <div class="vertical-align-box" v-else>
            <img class="card-info-image" src="@/assets/images/carriers-no-data.png" :class="{fadeinout : isLoadingCarrier}" />
            <p class="card-info-text" v-if="!isLoadingCarrier">
              {{ $t('carrier_graph_start_text')}}
            </p>
          </div>
        </div>
      </div>
      <div class="col-xs-4">
        <h2 class="m-b-20 section-title">{{ $t('Newest Shipments') }}</h2>
        <ul class="list-table" v-if="isNewestShipmentsGraph">
          <li
            class="list-table-item"
            @click="goToShipment(item.id)"
            v-for="(item, index) in get_newest_shipment_graph" :key="`item-${index}`">
            <div class="table-cell small">
              <div class="circled-icon flags" v-if="item.customer_address_country_id">
                <CountryFlag :country='getFlag(item.customer_address_country_id)' size='normal'/>
              </div>
            </div>
            <div class="table-cell">
              <p>
            <span class="main-info">
              {{ `${item.customer_company_name ? item.customer_company_name : item.customer_name}` }}
            </span>
                <span class="additional-info">{{ `${item.shipping_agent_name}` }} - {{ `${item.shipping_product_name}` }}</span>
              </p>
            </div>
          </li>
          <li class="list-table-item buttoned" @click="goToShipmentsPage">
            {{ $t('View all your shipments') }}
          </li>
        </ul>
        <div class="card p-40 vertical-align-box" style="height: 410px;" v-else>
          <img class="card-info-image" src="@/assets/images/newest-shipments-no-data.png" :class="{fadeinout : isLoadingShipments}" />
          <p class="card-info-text" v-if="!isLoadingShipments">
            {{ $t('no_shipment_dashboard_text') }}
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import { mapGetters } from 'vuex';
import VueApexCharts from 'vue-apexcharts'
import { VueDatamaps } from 'vue-mc360-datamaps'
import CountryFlag from "vue-country-flag";
import countryList from '@/data/country.js';
import ChartLegend from '@/components/global/chartLegend';
import formatRelativeWithOptions from 'date-fns/esm/fp/formatRelativeWithOptions/index';

/*setTimeout(() => {
  document.getElementById('deliveredMap').attributes['style'].textContent='background-color:'+ this.deliveredColor;
  document.getElementById('onTheWayMap').attributes['style'].textContent='background-color:'+ this.notDeliveredColor;
}, 2000)*/

export default {
  name: 'Dashboard',
  components: {VueApexCharts, VueDatamaps, CountryFlag, ChartLegend},
  data: function() {
    return {
      loaded: false,
      isLoadingActivity: true,
      isLoadingPerformance: true,
      isLoadingDeviation: true,
      isLoadingMap: true,
      isLoadingCarrier: true,
      isLoadingShipments: true,
      //PERFORMANCE CHART
      performanceChartsSeries: [],
      //CARRIERS CHART
      carriersChartLegend: [],
      carriersChartsSeries: [],
      // MAPS DATA
      projectionArea: 'world',
      updateMap: 0,
      geographyConfig: {
        popupOnHover: false,
        highlightOnHover: false,
        highlightFillColor: '#d3d5d8',
        highlightBorderColor: '#d3d5d8',
      },
      bubblesConfig: {
        popupTemplate: true,
        highlightOnHover: true,
        highlightFillColor: 'bubleFill',
        highlightBorderWidth: 0,
        borderWidth: 0,
        data: [],
      },
      popupData: {
        deliveredCount: '',
        onTheWayCount: ''
      },
      doBubbles: false,
    };
  },
  computed: {
    ...mapGetters([
      'get_activity_graph',
      'get_performance_graph',
      'get_carrier_graph',
      'get_map_graph',
      'get_deviations_graph',
      'get_newest_shipment_graph',
      'get_dummy_map',
      'get_admin_settings'
    ]),
    deliveredColor() {
      if (this.get_admin_settings?.admin_dashboard_settings?.delivered_color) {
        return this.get_admin_settings.admin_dashboard_settings.delivered_color
      }
      return '#E7E7E8';
    },
    notDeliveredColor() {
      if (this.get_admin_settings?.admin_dashboard_settings?.not_delivered_color) {
        return this.get_admin_settings.admin_dashboard_settings.not_delivered_color
      }
      return '#525252';
    },
    readyToShipColor() {
      if (this.get_admin_settings?.admin_dashboard_settings?.ready_to_ship_color) {
        return this.get_admin_settings.admin_dashboard_settings.ready_to_ship_color
      }
      return '#c3c3c3';
    },
    fills() {
      return {
        defaultFill: '#e6e7e8',
        bubleFill: this.notDeliveredColor
      }
    },
    colors() {
      let colors = [];
      for (const carrier in this.get_carrier_graph.carriers) {
        let carrierInfo = this.get_carrier_graph.carriers[carrier];
        colors.push(this.carrierColors(carrierInfo.agent_name));
      }
      return colors;
    },
    areaChartsSeries() {
      return [
        {
          name: this.$t('Not delivered yet'),
           type: 'area',
          data: this.get_activity_graph.not_delivered,
        },
        {
          name: this.$t('Delivered'),
          type: 'area',
          data: this.get_activity_graph.delivered,
        },
        {
          name: this.$t('Deviations'),
          type: 'area',
          data: this.get_activity_graph.deviations,
        }
      ]
    },
    areaChartLegend() {
      return [
        {
          text: this.$t('Not delivered yet'),
          background: this.notDeliveredColor,
        },
        {
          text: this.$t('Delivered'),
          background: this.deliveredColor,
        },
        {
          text: this.$t('Deviations'),
          background: '#ED5656',
        },
      ]
    },
    carriersChartOptions() {
      return {
        chart: {
          id: 'carriersChart',
          type: 'bar',
          height: 350,
          stacked: true,
          toolbar: {
            show: false,
          },
        },
        dataLabels: {
          enabled: false
        },
        plotOptions: {
          bar: {
            horizontal: false,
            borderRadius: 6
          },
        },
        xaxis: {
          type: 'category',
          categories: this.get_carrier_graph.months.map((month) => {
            return this.$t(month);
          })
        },
        legend: {
          show: false,
        },
        colors: this.colors,
        fill: {
          opacity: 1
        }
      }
    },
    areaChartOptions() {
      return {
        chart: {
          id: 'activityChart',
          toolbar: {
            show: false,
          },
        },
        dataLabels: {
          enabled: false
        },
        legend: {
          show: false
        },
        colors: [this.notDeliveredColor, this.deliveredColor, '#ED5656'],
        stroke: {
          curve: 'smooth',
          width: 3,
          colors: [this.notDeliveredColor, this.deliveredColor, '#ED5656'],
        },
        fill: {
          type: 'gradient',
          colors: [this.notDeliveredColor, this.deliveredColor, '#ED5656'],
          gradient: {
            shade: 'light',
            gradientFromColors: [this.notDeliveredColor, this.deliveredColor, '#ED5656'],
            shadeIntensity: 1,
            type: 'vertical',
            opacityFrom: 1,
            opacityTo: 0.7,
            stops: [0]
          },
        },
        xaxis: {
          categories: this.get_activity_graph.ship_date
        },
        yaxis: {
          forceNiceScale: false,
          decimalsInFloat: 0,
          //tickAmount: 4,
          labels: {
            formatter: function(val) {
              if(!val) {
                return 0
              }
              return Math.round(val)
            }
          },
        }
      }
    },
    performanceChartOptions() {
      return {
        chart: {
          id: 'performanceChart',
          type: 'donut',
        },
        colors: [this.deliveredColor, this.notDeliveredColor, this.readyToShipColor, '#ED5656'],
        labels: ['Delivered', 'In transit', 'Ready to Ship', 'Deviations',],
        dataLabels: {
          enabled: false
        },
        legend: false,
        states: {
          normal: {
            filter: {
              type: 'none',
              value: 0,
            }
          },
          hover: {
            filter: {
              type: 'darken',
              value: 0.8,
            }
          },

        }
      }
    },
    performanceChartLegend() {
      const array =  [
        {
          background: this.deliveredColor,
          mainText: this.$t('Delivered'),
          subText: this.$t('Shipments'),
          amount: '',
          percentage: '',
        },
        {
          background: this.notDeliveredColor,
          mainText: this.$t('In transit'),
          subText: this.$t('Shipments'),
          amount: '',
          percentage: '',
        },
        {
          background: this.readyToShipColor,
          mainText: this.$t('Ready to Ship'),
          subText: this.$t('Shipments'),
          amount: '',
          percentage: '',
        },
        {
          background: '#ED5656',
          mainText: this.$t('Deviations'),
          subText: this.$t('Shipments'),
          amount: '',
          percentage: '',
        },
      ]

      array[0].amount = this.get_performance_graph.delivered;
      array[0].percentage = this.get_performance_graph.delivered_percent + '%';
      array[1].amount = this.get_performance_graph.in_transit;
      array[1].percentage = this.get_performance_graph.in_transit_percent + '%';
      array[2].amount = this.get_performance_graph.ready_to_ship;
      array[2].percentage = this.get_performance_graph.ready_to_ship_percent + '%';
      array[3].amount = this.get_performance_graph.deviations;
      array[3].percentage = this.get_performance_graph.deviations_percent + '%';

      return array
    },
    isActivityGraph() {
      if(
        this.get_activity_graph.delivered.length == 0 &&
        this.get_activity_graph.not_delivered.length == 0 &&
        this.get_activity_graph.deviations.length == 0
      ) {
        return false;
      }
      return true;
    },
    isDeviationsGraph() {
      if(this.get_deviations_graph.length == 0) return false;
      return true;
    },
    isPerformanceGraph() {
      if(this.get_performance_graph.total_shipments == 0) return false;
      return true;
    },
    isMapGraph() {
      if(
        this.get_map_graph.continent_total.AF.total_delivery_by_continent == 0 &&
        this.get_map_graph.continent_total.AF.total_issue_by_continent == 0 &&
        this.get_map_graph.continent_total.AF.total_on_the_way_by_continent == 0 &&
        this.get_map_graph.continent_total.AN.total_delivery_by_continent == 0 &&
        this.get_map_graph.continent_total.AN.total_issue_by_continent == 0 &&
        this.get_map_graph.continent_total.AN.total_on_the_way_by_continent == 0 &&
        this.get_map_graph.continent_total.AS.total_delivery_by_continent == 0 &&
        this.get_map_graph.continent_total.AS.total_issue_by_continent == 0 &&
        this.get_map_graph.continent_total.AS.total_on_the_way_by_continent == 0 &&
        this.get_map_graph.continent_total.EU.total_delivery_by_continent == 0 &&
        this.get_map_graph.continent_total.EU.total_issue_by_continent == 0 &&
        this.get_map_graph.continent_total.EU.total_on_the_way_by_continent == 0 &&
        this.get_map_graph.continent_total.NA.total_delivery_by_continent == 0 &&
        this.get_map_graph.continent_total.NA.total_issue_by_continent == 0 &&
        this.get_map_graph.continent_total.NA.total_on_the_way_by_continent == 0 &&
        this.get_map_graph.continent_total.SA.total_delivery_by_continent == 0 &&
        this.get_map_graph.continent_total.SA.total_on_the_way_by_continent == 0 &&
        this.get_map_graph.continent_total.SA.total_issue_by_continent == 0
      ) {
        return false;
      }
      return true;
    },
    isCarrierGraph() {
      if(this.get_carrier_graph.carriers == 0) return false;
      return true;
    },
    isNewestShipmentsGraph() {
      if(this.get_newest_shipment_graph.length == 0) return false;
      return true;
    },
  },
  mounted(){
    // console.log(this)
    let self = this;
    setTimeout(() => {
      if(self.isMapGraph){
        self.doBubbles = true;
      }
    }, 3000)
  },
  created() {
    this.$store.dispatch('GET_CHART', {type : 'performance_graph'}).then((res) => {
      this.isLoadingPerformance = false;
      // Performance data
      this.performanceChartsSeries.push(this.get_performance_graph.delivered);
      this.performanceChartsSeries.push(this.get_performance_graph.in_transit);
      this.performanceChartsSeries.push(this.get_performance_graph.ready_to_ship);
      this.performanceChartsSeries.push(this.get_performance_graph.deviations);
    });

    this.$store.dispatch('GET_ADMIN_SETTINGS', window.location.hostname).finally(() => {
      // OPALI RUTU ZA PODATKE
      this.$store.dispatch('GET_CHART', {type : 'activity_graph'}).then((res) => {
        this.isLoadingActivity = false;
        // activity data
      });

      this.$store.dispatch('GET_CHART', {type : 'carrier_graph'}).then((res) => {
        // Carrier data
        var indexCount = 0;

        for (const carrier in this.get_carrier_graph.carriers) {
          let carrierInfo = this.get_carrier_graph.carriers[carrier];
          this.carriersChartsSeries.push({
            name: carrierInfo.agent_name,
            data: [],
          });
          this.carriersChartLegend.push({
            text: carrierInfo.agent_name,
            background: this.carrierColors(carrierInfo.agent_name),
          })
          this.get_carrier_graph.months.forEach((month) => {
            this.carriersChartsSeries[indexCount].data.push(carrierInfo.shipments_by_month[month].shipment_count)
          })
          indexCount++;
        }
        this.isLoadingCarrier = false;
      });
      this.$store.dispatch('GET_CHART', {type : 'map_graph'}).then((res) => {
        this.isLoadingMap = false;
        // Map data
        this.buildContinetData(this.get_map_graph.continent_total);
      });

      this.$store.dispatch('GET_CHART', {type : 'deviation_graph'}).then(() => this.isLoadingDeviation = false);
      this.$store.dispatch('GET_CHART', {type : 'newest_shipment_graph'}).then(() => this.isLoadingShipments = false);
      this.loaded = true;
    })
  },
  methods: {
    getDeviationsQueryString() {
      return '{"status":{"value":"16","additional":""}}';
    },
    getStatusMessageText(message) {
      //Translate first index of array
      if (message && message.includes('@separator0@')) {
        const statusMsgArray = message.split('@separator0@');
        return `${this.$t(statusMsgArray[0])} ${statusMsgArray[1]}`;
      }

      if (message && message.includes('#')) {
        const statusMsgArray = message.split('#');
        const translationString = statusMsgArray[0] ? statusMsgArray[0].trim() : '';
        return `${this.$t(translationString)} #${statusMsgArray[1]}`;
      }

      return `${this.$t(message)}`
    },
    carrierColors(carrierName) {
      switch (carrierName) {
        case 'PostNord':
          return "#0099BD";
          break;
        case 'GLS':
          return "#0713B7";
          break;
        case 'GLS Express':
          return "#1420ca";
          break;
        case 'Bring':
          return "#6AA030";
          break;
        case 'Dao365':
          return "#FF010B";
          break;
        case 'DHL':
          return "#FFCC00";
          break;
        case 'DHL Express':
          return "#FFCC00";
          break;
        case 'UPS':
          return "#330E00";
          break;
        case 'FedEx':
          return "#590C90";
          break;
        case 'Danske Fragtmænd':
          return "#3D96D4";
          break;
        case 'Danske Fragtmænd Express':
          return "#45a2e3";
          break;
        case 'DSV':
          return "#002066";
          break;
        case 'DSV Xpress':
          return "#002066";
          break;
        case 'Mini Trans':
          return "#FF7224";
          break;
        case 'Burd Delivery':
          return "#ED005B";
          break;
        case 'Custom Carrier':
          return "#0099DA";
          break;
        case 'Dachser':
          return "#FBBA00";
          break;
        default:
          return "#0099DA";
      }
    },
    buildContinetData(res) {
      let continents = res;
      // let continents = this.get_dummy_map.continent_total;
      let nameNumber = 1;
      for(const element in continents){
        if(
          element != 'AN' &&
          (continents[element].total_delivery_by_continent != 0 || continents[element].total_issue_by_continent != 0 || continents[element].total_on_the_way_by_continent != 0)
        ) {
          this.bubblesConfig.data.push({
            name: `bubble${nameNumber}`,
            radius: 35,
            centered: this.centerContinentBubble(element),
            fillKey: 'bubleFill',
            label: `${element}`,
            bubbleText: `${continents[element].total_delivery_by_continent + continents[element].total_on_the_way_by_continent}`,
            deviationText: `${continents[element].total_issue_by_continent}`,
            // popup data
            deliveredCount: `${continents[element].total_delivery_by_continent}`,
            onTheWayCount: `${continents[element].total_on_the_way_by_continent}`,
            tooltipTitle: this.getContinentName(element),
          })
          nameNumber++;
        }
      }
    },
    zoomToWorld() {
      this.bubblesConfig.data = [];
      this.buildContinetData(this.get_map_graph.continent_total);
      this.projectionArea = 'world';
      this.updateMap++;
    },
    popupTemplate ({ datum }) {
      this.popupData = {
        deliveredCount: datum.deliveredCount,
        onTheWayCount: datum.onTheWayCount,
        tooltipTitle: datum.tooltipTitle,
        deviations: datum.deviationText,
      }
    },
    centerContinentBubble(continent) {
      if(continent == 'AF') return 'COG';
      if(continent == 'AN') return 'COG';
      if(continent == 'AS') return 'CHN';
      if(continent == 'EU') return 'DEU';
      if(continent == 'NA') return 'USA';
      if(continent == 'SA') return 'BRA';
      if(continent == 'OC') return 'AUS';
    },
    getContinentName(continent) {
      if(continent == 'AF') return 'Africa';
      if(continent == 'AN') return 'COG';
      if(continent == 'AS') return 'Asia';
      if(continent == 'EU') return 'Europe';
      if(continent == 'NA') return 'North America';
      if(continent == 'SA') return 'South America';
      if(continent == 'OC') return 'Australia';
    },
    goToShipment(id){
      this.$router.push(`/view-shipment/${id}`)
    },
    goToShipmentsPage() {
      this.$router.push('/shipments/outbound')
    },
    handleBubbleClick(val) {
      let zoomedCase = false;
      switch (val.item.label) {
        case 'EU':
          this.projectionArea = 'europe';
          zoomedCase = true;
          break;
        case 'AF':
          this.projectionArea = 'africa';
          zoomedCase = true;
          break;
        case 'AS':
          this.projectionArea = 'asia';
          zoomedCase = true;
          break;
        case 'NA':
          this.projectionArea = 'north';
          zoomedCase = true;
          break;
        case 'SA':
          this.projectionArea = 'south';
          zoomedCase = true;
          break;
        case 'OC':
          this.projectionArea = 'australia';
          zoomedCase = true;
          break;
        default:
          this.projectionArea = 'world';
          zoomedCase = false;
      }
      if( val.item.label == 'EU') this.projectionArea = 'europe';
      if( val.item.label == 'AF') this.projectionArea = 'africa';
      if( val.item.label == 'AS') this.projectionArea = 'asia';
      if( val.item.label == 'NA') this.projectionArea = 'north';
      if( val.item.label == 'SA') this.projectionArea = 'south';
      if( val.item.label == 'OC') this.projectionArea = 'australia';
      if(zoomedCase){
        this.bubblesConfig.data = [];
        let nameNumber = 1;
        let loopObject = this.get_map_graph[`${val.item.label}`];
        for(const element in loopObject){
          this.bubblesConfig.data.push({
            name: `bubble-country-${nameNumber}`,
            radius: 25,
            centered: `${loopObject[element].country_alpha3}`,
            fillKey: 'bubleFill',
            label: `${element}`,
            bubbleText: `${loopObject[element].delivered_count + loopObject[element].on_the_way_count}`,
            deviationText: `${loopObject[element].issue_count}`,
            // popup data
            deliveredCount: `${loopObject[element].delivered_count}`,
            onTheWayCount: `${loopObject[element].on_the_way_count}`,
            tooltipTitle: `${loopObject[element].country_name}`,
          })
          nameNumber++;
        }
        this.updateMap++;
      } else {
        this.zoomToWorld();
      }
    },
    setProjection (type) {
      const createProjection = {
        europe: function (d3, element) {
          const projection = d3.geoMercator().center([0, 0])
            .rotate([-10, -55])
            .scale(element.offsetWidth * 1)
            .translate([element.offsetWidth / 2, element.offsetHeight / 2])
          const path = d3.geoPath().projection(projection)
          return { projection, path }
        },
        africa: function (d3, element) {
          const projection = d3.geoMercator().center([0, 0])
            .center([23, -2])
            .rotate([4.4, 0])
            .scale(400)
            .translate([element.offsetWidth / 2, element.offsetHeight / 2]);
          const path = d3.geoPath().projection(projection)
          return { projection, path }
        },
        asia: function (d3, element) {
          const projection = d3.geoMercator().center([0, 0])
            .center([100, 30])
            .rotate([4.4, 0])
            .scale(300)
            .translate([element.offsetWidth / 2, element.offsetHeight / 2]);
          const path = d3.geoPath().projection(projection)
          return { projection, path }
        },
        north: function (d3, element) {
          const projection = d3.geoMercator().center([0, 0])
            .center([-100, 40])
            .rotate([4.4, 0])
            .scale(400)
            .translate([element.offsetWidth / 2, element.offsetHeight / 2]);
          const path = d3.geoPath().projection(projection)
          return { projection, path }
        },
        south: function (d3, element) {
          const projection = d3.geoMercator().center([0, 0])
            .center([-60, -30])
            .rotate([4.4, 0])
            .scale(350)
            .translate([element.offsetWidth / 2, element.offsetHeight / 2]);
          const path = d3.geoPath().projection(projection)
          return { projection, path }
        },
        australia: function (d3, element) {
          const projection = d3.geoMercator().center([0, 0])
            .center([120, -30])
            .rotate([4.4, 0])
            .scale(400)
            .translate([element.offsetWidth / 2, element.offsetHeight / 2]);
          const path = d3.geoPath().projection(projection)
          return { projection, path }
        },
        zoom: function (d3, element) {
          var projection = d3.geoMercator()
            .center([0, 0])
            .rotate([0,0])
            .scale(1, 1)
            .translate([0,0])
          var path = d3.geoPath()
            .projection(projection)
          return { path: path, projection: projection }
        }
      }
      return createProjection[type]
    },
    getFlag(id) {
      return countryList.filter((opt) => opt.id === id.toString())[0].alpha2;
    },
  }
};
</script>
<style>
.fadeinout
{
  animation: fadeinout 3s infinite;
}

@keyframes fadeinout
{
  0%{
    opacity:0;
  }
  50%
  {
    opacity:1;
  }
  100%
  {
    opacity:0;
  }
}
</style>
